import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { ViewMode } from '@/sections/Workspace/types';
import { useRecoilState } from 'recoil';

import { filterQueryState, viewModeState } from './atoms';
import { WorkspaceSections } from './types';

function useViewMode(section: WorkspaceSections, minimal: boolean = false) {
  const [viewMode, setViewMode] = useRecoilState(viewModeState(section));

  return { viewMode: minimal ? ViewMode.List : viewMode, setViewMode };
}

function useFilterQuery(section: WorkspaceSections) {
  const [filterQuery, setFilterQuery] = useRecoilState(filterQueryState(section));

  return { filterQuery, setFilterQuery };
}

function useForceUpdateOnWindowResize(debounceMs: number = 100) {
  const [forceUpdateKey, setForceUpdateKey] = useState(0);

  const debouncedResize = useMemo(
    () => debounce(() => setForceUpdateKey((prev) => prev + 1), debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      debouncedResize.cancel();
    };
  }, [debouncedResize]);

  return forceUpdateKey;
}

function useTableContainerHeight(headerRef: React.RefObject<HTMLDivElement>, key: number): number {
  const [height, setHeight] = useState(0);
  const forceUpdateKey = useForceUpdateOnWindowResize();

  const measureHeight = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      setHeight(element.clientHeight);
    }
  }, []);

  useLayoutEffect(() => {
    if (headerRef.current) {
      measureHeight(headerRef.current);
    }
  }, [measureHeight, headerRef, key, forceUpdateKey]);

  return height;
}

export { useViewMode, useFilterQuery, useTableContainerHeight };
